import { useEffect, useState, RefObject } from 'react';

interface OffsetDimensions {
  width: number;
  height: number;
  left: number;
  top: number;
}

/**
 * Hook to track the offset dimensions of a DOM element referenced by a ref.
 * Automatically updates dimensions on window resize events.
 *
 * @param containerRef - React ref object pointing to the element to measure
 * @returns Object containing width, height, left, and top offset values
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { width, height, left, top } = useRefOffsetDimensions(containerRef);
 *
 * return <div ref={containerRef}>Width: {width}px</div>;
 */
const useRefOffsetDimensions = (containerRef: RefObject<HTMLElement | null>): OffsetDimensions => {
  const [dimensions, setDimensions] = useState<OffsetDimensions>({
    width: 0,
    height: 0,
    left: 0,
    top: 0
  });

  useEffect(() => {
    const getDimensions = (): OffsetDimensions => {
      if (!containerRef.current) {
        return { width: 0, height: 0, left: 0, top: 0 };
      }

      return {
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
        left: containerRef.current.offsetLeft,
        top: containerRef.current.offsetTop,
      };
    };

    const handleResize = () => {
      setDimensions(getDimensions());
    };

    if (containerRef.current) {
      setDimensions(getDimensions());
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  return dimensions;
};

export default useRefOffsetDimensions;
